import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Library, Package, RefreshCw, Bell, ClipboardCheck } from "lucide-react";

export function QuotationsList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (user.role === "factory_customer") {
      api.get("/requests").then((r) => {
        const withQuotes = (r.data.requests || []).filter((x) =>
          ["quote_ready", "awaiting_approval", "approved"].includes(x.status)
        );
        setItems(withQuotes);
      });
    } else if (user.role === "workshop_partner") {
      api.get("/workshop-quotations").then((r) => setItems(r.data.quotations || []));
    }
  }, [user.role]);

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">عروض الأسعار</div>
        <h1 className="text-3xl font-bold text-brand-navy">
          {user.role === "workshop_partner" ? "عروض الأسعار المقدمة" : "عروض الأسعار الواردة"}
        </h1>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="px-4 py-3 text-start">الرقم</th>
                <th className="px-4 py-3 text-start">القطعة</th>
                {user.role === "workshop_partner" && <th className="px-4 py-3 text-start">الإجمالي</th>}
                <th className="px-4 py-3 text-start">الحالة</th>
                <th className="px-4 py-3 text-start"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q.id} className="border-t border-slate-100" data-testid={`quotation-row-${q.id}`}>
                  <td className="px-4 py-3 font-semibold text-brand-navy">{q.quotation_number || q.request_number}</td>
                  <td className="px-4 py-3">{q.part_name || "—"}</td>
                  {user.role === "workshop_partner" && <td className="px-4 py-3 font-bold">{q.total_price?.toLocaleString()} ر.س</td>}
                  <td className="px-4 py-3"><StatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-end">
                    {user.role === "factory_customer" && <Link to={`/app/requests/${q.id}`} className="text-brand-amber font-semibold">عرض التفاصيل</Link>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-500">لا توجد عروض بعد.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function PartLibrary() {
  const [parts, setParts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [reqDate, setReqDate] = useState("");

  const load = () => api.get("/part-library").then((r) => setParts(r.data.parts || []));
  useEffect(() => { load(); }, []);

  const reorder = async () => {
    try {
      await api.post("/part-library/reorder", {
        part_library_id: selected.id,
        quantity: Number(qty),
        required_date: reqDate || null,
      });
      toast.success("تم إنشاء طلب إعادة التصنيع بنجاح");
      setSelected(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">مكتبة القطع</div>
        <h1 className="text-3xl font-bold text-brand-navy">أرشيف القطع المصنعة سابقاً</h1>
        <p className="text-slate-600 mt-1">اطلب إعادة تصنيع أي قطعة سبق تصنيعها بضغطة زر.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {parts.map((p) => (
          <div key={p.id} className="card-industrial p-5" data-testid={`part-card-${p.part_number}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-brand-amber/10 flex items-center justify-center">
                <Package className="w-5 h-5 text-brand-amber" />
              </div>
              <div>
                <div className="font-bold text-brand-navy">{p.part_name}</div>
                <div className="text-xs text-slate-500">{p.part_number}</div>
              </div>
            </div>
            <div className="text-sm space-y-1 mb-4">
              <div><span className="text-slate-500">المادة:</span> {p.material}</div>
              <div><span className="text-slate-500">الأبعاد:</span> {p.dimensions}</div>
              <div><span className="text-slate-500">آخر كمية:</span> {p.previous_quantity}</div>
              <div><span className="text-slate-500">آخر سعر:</span> {p.last_price?.toLocaleString()} ر.س</div>
            </div>
            <Button className="btn-cta w-full" onClick={() => setSelected(p)} data-testid={`reorder-btn-${p.part_number}`}>
              <RefreshCw className="w-4 h-4 me-1" /> إعادة تصنيع القطعة
            </Button>
          </div>
        ))}
      </div>
      {parts.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="empty-library">
          <Library className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-500">لا توجد قطع في المكتبة بعد. سيتم إضافة القطع تلقائياً بعد اكتمال أول طلب.</div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>إعادة تصنيع: {selected?.part_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>الكمية الجديدة</Label><Input type="number" min={1} value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1.5" data-testid="reorder-qty" /></div>
            <div><Label>التاريخ المطلوب</Label><Input type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} className="mt-1.5" data-testid="reorder-date" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setSelected(null)}>إلغاء</Button>
            <Button className="btn-cta" onClick={reorder} data-testid="submit-reorder-btn">إرسال طلب إعادة التصنيع</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export function NotificationsList() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/notifications").then((r) => setItems(r.data.notifications || [])); }, []);
  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
  };
  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">الإشعارات</div>
        <h1 className="text-3xl font-bold text-brand-navy">جميع الإشعارات</h1>
      </div>
      <div className="grid gap-2" data-testid="notifications-list">
        {items.map((n) => (
          <button key={n.id} onClick={() => markRead(n.id)} className={`text-start w-full card-industrial p-4 flex items-start gap-3 ${!n.is_read ? "border-brand-amber" : ""}`} data-testid={`notification-${n.id}`}>
            <Bell className="w-5 h-5 text-brand-amber shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-brand-navy">{n.title_ar}</div>
              <div className="text-sm text-slate-600">{n.message_ar}</div>
              <div className="text-xs text-slate-400 mt-1">{n.created_at?.slice(0, 16).replace("T", " ")}</div>
            </div>
            {!n.is_read && <span className="w-2 h-2 rounded-full bg-brand-amber shrink-0 mt-1" />}
          </button>
        ))}
        {items.length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">لا توجد إشعارات.</div>}
      </div>
    </DashboardLayout>
  );
}
